import { ref, computed } from "vue"

export interface TaxRate {
  state: string
  stateRate: number
  combinedRate: number
}

const STORAGE_KEY = "taxRates"
const TIMESTAMP_KEY = "taxRatesFetchedAt"
const CACHE_TTL = 1000 * 60 * 60 * 24 // 24 hours

const taxRates = ref<TaxRate[]>([])
const isLoading = ref(false)
const error = ref<string | null>(null)
const searchQuery = ref("")

export function useTaxRates() {
  const load = async () => {
    isLoading.value = true
    error.value = null
    try {
      const cached = localStorage.getItem(STORAGE_KEY)
      const timestamp = localStorage.getItem(TIMESTAMP_KEY)
      const isFresh = timestamp && Date.now() - parseInt(timestamp) < CACHE_TTL

      if (cached && isFresh) {
        const parsed = JSON.parse(cached)
        if (Array.isArray(parsed) && parsed.length > 0) {
          taxRates.value = parsed
          return
        }
      }

      const res = await fetch("/api/tax-rates")
      if (!res.ok) throw new Error("Failed to fetch tax rates")
      const data = await res.json()
      if (Array.isArray(data) && data.length > 0) {
        taxRates.value = data
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
        localStorage.setItem(TIMESTAMP_KEY, Date.now().toString())
      } else {
        throw new Error("Received empty dataset")
      }
    } catch (err: any) {
      error.value = err.message || "Unknown error"
    } finally {
      isLoading.value = false
    }
  }

  const clear = () => {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(TIMESTAMP_KEY)
    taxRates.value = []
  }

  const refresh = async () => {
    localStorage.removeItem(TIMESTAMP_KEY)
    await load()
  }

  const filtered = computed(() => {
    if (!searchQuery.value) return taxRates.value
    const q = searchQuery.value.toLowerCase()
    return taxRates.value.filter(rate =>
      rate.state.toLowerCase().includes(q)
    )
  })

  const normalizeState = (s: string) => s.replace(/\s\([a-z]\)$/i, "")

  const getCombinedRateByState = async (stateName: string): Promise<number | null> => {
    if (taxRates.value.length === 0) {
      await load()
    }
    if (taxRates.value.length === 0) return null
    const clean = normalizeState(stateName)
    const match = taxRates.value.find(
      r => r.state.toLowerCase() === clean.toLowerCase()
    )
    return match ? match.combinedRate : null
  }

  return {
    taxRates: filtered,
    rawRates: taxRates,
    isLoading,
    error,
    searchQuery,
    load,
    refresh,
    clear,
    getCombinedRateByState,
  }
}